import { useVideoCreatorStore } from '@src/stores-v2/VideoCreatorStoreContext';
import React, { useEffect, useRef } from 'react';
import styled from 'styled-components';

import Modal from '../common/Modal';

interface DebugModalProps {
  onClose: () => void;
  content: any;
  title: string;
}

const DebugModal: React.FC<DebugModalProps> = ({ content, onClose, title }) => {
  const videoCreator = useVideoCreatorStore();
  const modalRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (modalRef.current) {
      modalRef.current.focus();
    }
  }, []);

  const prettyPrintJson = (data: any) => {
    try {
      return JSON.stringify(data, null, 2);
    } catch (error) {
      return String(data);
    }
  };

  const copyToClipboard = () => {
    navigator.clipboard.writeText(prettyPrintJson(content)).then(() => {
      videoCreator.showToast({
        state: 'success',
        message: 'Debug JSON copied',
      });
    });
  };

  return (
    <Modal isOpen={true} closeModal={onClose}>
      <ModalWrapper ref={modalRef} tabIndex={-1}>
        <DebugHeader>
          <DebugHeading>{title}</DebugHeading>
        </DebugHeader>
        <DebugContent>
          <pre>{prettyPrintJson(content)}</pre>
        </DebugContent>
        <CopyLinkContainer>
          <CopyLink onClick={copyToClipboard}>Copy to clipboard</CopyLink>
        </CopyLinkContainer>
      </ModalWrapper>
    </Modal>
  );
};

const ModalWrapper = styled.div`
  background-color: var(--background);
  color: var(--text-secondary);
  width: 600px;
  max-height: 80vh;
  overflow-y: auto;
  box-shadow: var(--elevation-toast);
  border-radius: 10px;
  padding: 20px;
  border: 1px solid var(--border);
  display: flex;
  flex-direction: column;
`;

const DebugHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 16px;
`;

const DebugHeading = styled.h2`
  font-size: 18px;
  color: var(--accent);
`;

const DebugContent = styled.div`
  white-space: pre-wrap;
  background-color: var(--surface);
  color: var(--text-secondary);
  padding: 16px;
  border-radius: 8px;
  font-size: var(--text-xs);
  overflow: auto;
  text-align: left;
  position: relative;
`;

const CopyLinkContainer = styled.div`
  position: relative;
  display: flex;
  align-items: center;
`;

const CopyLink = styled.a`
  position: absolute;
  bottom: 10px;
  right: 10px;
  color: var(--accent);
  cursor: pointer;
  font-size: var(--text-sm);
  &:hover {
    color: #dfb615;
  }
`;

export default DebugModal;
